<template>
    <div
        ref="toolbarRoot"
        class="fixed z-[80] select-none"
        :style="{ left: pos.x + 'px', top: pos.y + 'px' }"
        @mouseenter="onRootEnter"
        @mouseleave="onRootLeave"
    >
        <!-- Draggable trigger -->
        <div
            class="drawing-toolbar-trigger"
            :class="{ 'is-active': menuOpen || isDrawing }"
            @pointerdown="onTriggerPointerDown"
            @mouseenter="onTriggerEnter"
            @click.stop="onTriggerClick"
        >
            <PencilIcon class="size-6" />
        </div>

        <!-- Circular menu -->
        <TransitionGroup
            tag="div"
            class="drawing-toolbar-menu"
            :css="false"
            @enter="onItemEnter"
            @leave="onItemLeave"
        >
            <button
                v-for="(item, index) in visibleCircularItems"
                :key="item.id"
                type="button"
                class="drawing-toolbar-item"
                :class="{ 'is-active': item.active }"
                :style="circleItemStyle(index, circularItems.length)"
                :data-spread="spreadTransform(index, circularItems.length)"
                :data-index="index"
                :data-count="circularItems.length"
                :ref="item.id === 'color' ? setColorTrigger : undefined"
                :title="item.label"
                @click.stop="item.action"
            >
                <component :is="item.icon" class="size-5" />
            </button>
        </TransitionGroup>

        <!-- Linear menu (alternative mode) -->
        <div
            v-if="menuOpen && mode === 'linear'"
            class="drawing-toolbar-linear"
        >
            <button
                type="button"
                class="drawing-toolbar-item"
                :class="{ 'is-active': isDrawing }"
                title="Toggle drawing"
                @click.stop="toggleDraw"
            >
                <PencilIcon class="size-5" />
            </button>
            <button
                type="button"
                class="drawing-toolbar-item"
                :class="{ 'is-active': saveDraw }"
                title="Enable save drawing"
                @click.stop="toggleSave"
            >
                <BookmarkIcon class="size-5" />
            </button>
            <button
                type="button"
                class="drawing-toolbar-item"
                title="Clear drawing"
                @click.stop="clearCanvas"
            >
                <TrashIcon class="size-5" />
            </button>
            <button
                type="button"
                ref="colorTrigger"
                class="drawing-toolbar-item"
                :style="{ backgroundColor: brushColor }"
                title="Brush color"
                @click.stop="openColorPanel"
            >
                <SwatchIcon class="size-5 text-white mix-blend-difference" />
            </button>
            <label class="drawing-toolbar-slider-label" title="Brush size">
                <AdjustmentsHorizontalIcon class="size-5" />
                <input
                    type="range"
                    min="1"
                    max="20"
                    v-model.number="brushSize"
                    class="drawing-toolbar-slider"
                />
            </label>
            <button
                type="button"
                class="drawing-toolbar-item"
                title="Close free-hand mode"
                @click.stop="closeToolbar"
            >
                <XMarkIcon class="size-5" />
            </button>
        </div>

        <!-- Brush size panel -->
        <div
            v-if="panel === 'brush'"
            class="drawing-toolbar-panel"
        >
            <label class="text-xs text-gray-300">Brush Size</label>
            <input
                type="range"
                min="1"
                max="20"
                v-model.number="brushSize"
                class="drawing-toolbar-slider w-24"
            />
            <span class="text-xs text-gray-300">{{ brushSize }}px</span>
        </div>

        <ColorPicker
            ref="colorPicker"
            :trigger-element="colorTrigger"
            @applyColor="applyColor"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits, defineExpose } from 'vue'
import {
    PencilIcon,
    BookmarkIcon,
    TrashIcon,
    SwatchIcon,
    AdjustmentsHorizontalIcon,
    XMarkIcon,
} from '@heroicons/vue/24/outline'
import ColorPicker from '@/components/common/elements/ColorPicker.vue'

const props = defineProps({
    mode: {
        type: String,
        default: 'circular',
        validator: (value) => ['circular', 'linear'].includes(value),
    },
    canvasRef: {
        type: Object,
        default: null,
    },
    targetSelector: {
        type: String,
        default: '.song-viewer',
    },
    zoomValue: {
        type: Number,
        default: 1,
    },
})

const emits = defineEmits(['close', 'drawingChanged', 'saveChanged'])

const toolbarRoot = ref(null)
const colorPicker = ref(null)
const colorTrigger = ref(null)

const pos = ref({ x: 0, y: 0 })
const menuOpen = ref(false)
const panel = ref(null)
const colorOpen = ref(false)
const isDrawing = ref(true)
const saveDraw = ref(false)
const brushSize = ref(3)
const brushColor = ref('#000000')

const isTouchDevice = ref('ontouchstart' in window || navigator.maxTouchPoints > 0)

const CIRCULAR_RADIUS = 74
const ITEM_ANIMATION_DURATION = 200

const circularItems = computed(() => [
    { id: 'draw', icon: PencilIcon, label: 'Toggle drawing', active: isDrawing.value, action: toggleDraw },
    { id: 'save', icon: BookmarkIcon, label: 'Save drawing', active: saveDraw.value, action: toggleSave },
    { id: 'clear', icon: TrashIcon, label: 'Clear drawing', active: false, action: clearCanvas },
    { id: 'color', icon: SwatchIcon, label: 'Brush color', active: colorOpen.value, action: openColorPanel },
    { id: 'brush', icon: AdjustmentsHorizontalIcon, label: 'Brush size', active: panel.value === 'brush', action: openBrushPanel },
    { id: 'close', icon: XMarkIcon, label: 'Close free-hand mode', active: false, action: closeToolbar },
])

const visibleCircularItems = computed(() => {
    if (!menuOpen.value || props.mode !== 'circular') return []
    return circularItems.value
})

const spreadTransform = (index, total) => {
    const angle = (index / total) * Math.PI * 2 - Math.PI / 2
    const x = Math.cos(angle) * CIRCULAR_RADIUS
    const y = Math.sin(angle) * CIRCULAR_RADIUS
    return `translate(${x}px, ${y}px)`
}

const circleItemStyle = (index, total) => {
    return {
        transform: spreadTransform(index, total),
    }
}

const onItemEnter = (el, done) => {
    const index = Number(el.dataset.index || 0)
    const delay = index * 30
    const spread = el.dataset.spread || 'translate(0, 0)'
    el.style.transition = 'none'
    el.style.opacity = '0'
    el.style.transform = 'translate(0, 0) scale(0.6)'
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            el.style.transition = `opacity ${ITEM_ANIMATION_DURATION}ms ease ${delay}ms, transform ${ITEM_ANIMATION_DURATION}ms ease ${delay}ms`
            el.style.opacity = '1'
            el.style.transform = spread
        })
    })
    setTimeout(done, ITEM_ANIMATION_DURATION + delay + 30)
}

const onItemLeave = (el, done) => {
    const index = Number(el.dataset.index || 0)
    const count = Number(el.dataset.count || circularItems.value.length)
    const delay = (count - 1 - index) * 30
    el.style.transition = `opacity ${ITEM_ANIMATION_DURATION}ms ease ${delay}ms, transform ${ITEM_ANIMATION_DURATION}ms ease ${delay}ms`
    el.style.opacity = '0'
    el.style.transform = 'translate(0, 0) scale(0.5)'
    setTimeout(done, ITEM_ANIMATION_DURATION + delay + 30)
}

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

const setColorTrigger = (element) => {
    if (element) {
        colorTrigger.value = element
    }
}

const getCanvas = () => props.canvasRef?.value || props.canvasRef

const syncCanvas = () => {
    getCanvas()?.setBrushSize(brushSize.value)
    getCanvas()?.setBrushColor(brushColor.value)
}

watch(brushSize, (value) => {
    getCanvas()?.setBrushSize(value)
})

const toggleDraw = () => {
    isDrawing.value = !isDrawing.value
    getCanvas()?.toggleDrawing()
    emits('drawingChanged', isDrawing.value)
    panel.value = null
}

const toggleSave = () => {
    saveDraw.value = !saveDraw.value
    getCanvas()?.toggleSaveDraw()
    emits('saveChanged', saveDraw.value)
    panel.value = null
}

const clearCanvas = () => {
    getCanvas()?.clearCanvas()
    panel.value = null
}

const openBrushPanel = () => {
    panel.value = panel.value === 'brush' ? null : 'brush'
}

const openColorPanel = () => {
    colorOpen.value = !colorOpen.value
    panel.value = null
    if (colorOpen.value) {
        setTimeout(() => {
            colorPicker.value?.toggle()
        }, 50)
    } else {
        colorPicker.value?.close()
    }
}

const applyColor = (color) => {
    brushColor.value = color
    getCanvas()?.setBrushColor(color)
    colorOpen.value = false
    panel.value = null
}

const closing = ref(false)

const closeToolbar = () => {
    if (closing.value) return
    closing.value = true
    panel.value = null
    colorOpen.value = false
    colorPicker.value?.close()

    if (props.mode === 'circular') {
        // Animate the menu items out first, then close once the effect is done
        menuOpen.value = false
        const closingDelay = ITEM_ANIMATION_DURATION + (circularItems.value.length - 1) * 30 + 30
        setTimeout(() => {
            emits('close')
        }, closingDelay)
    } else {
        emits('close')
    }
}

// ---- Drag handling ----
const dragState = ref(null)

const onTriggerPointerDown = (event) => {
    event.preventDefault()
    event.stopPropagation()
    clearTimeout(hoverTimer.value)
    dragState.value = {
        startX: event.clientX,
        startY: event.clientY,
        originX: pos.value.x,
        originY: pos.value.y,
        moved: false,
    }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
}

const onPointerMove = (event) => {
    const state = dragState.value
    if (!state) return
    const dx = event.clientX - state.startX
    const dy = event.clientY - state.startY
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
        state.moved = true
    }
    if (state.moved) {
        pos.value = {
            x: clamp(state.originX + dx, 0, window.innerWidth - 52),
            y: clamp(state.originY + dy, 0, window.innerHeight - 52),
        }
        menuOpen.value = false
        panel.value = null
        colorOpen.value = false
        colorPicker.value?.close()
    }
}

const onPointerUp = () => {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
}

const onTriggerClick = () => {
    if (dragState.value?.moved) return
    menuOpen.value = !menuOpen.value
    if (!menuOpen.value) {
        panel.value = null
        colorOpen.value = false
        colorPicker.value?.close()
    }
}

// ---- Hover open/close ----
const hoverTimer = ref(null)
const closeTimer = ref(null)

const onTriggerEnter = () => {
    if (isTouchDevice.value) return
    clearTimeout(hoverTimer.value)
    hoverTimer.value = setTimeout(() => {
        menuOpen.value = true
    }, 200)
}

const onRootEnter = () => {
    clearTimeout(closeTimer.value)
}

const onRootLeave = () => {
    clearTimeout(hoverTimer.value)
    scheduleClose()
}

const scheduleClose = () => {
    clearTimeout(closeTimer.value)
    closeTimer.value = setTimeout(() => {
        menuOpen.value = false
        panel.value = null
        colorOpen.value = false
        colorPicker.value?.close()
    }, 300)
}

// ---- Click outside (touch) ----
const onClickOutside = (event) => {
    if (!menuOpen.value) return
    if (toolbarRoot.value && toolbarRoot.value.contains(event.target)) return
    menuOpen.value = false
    panel.value = null
    colorOpen.value = false
    colorPicker.value?.close()
}

// ---- Positioning ----
const positionAtSongViewer = () => {
    const target = document.querySelector(props.targetSelector)
    if (target) {
        const rect = target.getBoundingClientRect()
        pos.value = {
            x: clamp(rect.right - 60, 0, window.innerWidth - 52),
            y: clamp(rect.top + 16, 0, window.innerHeight - 52),
        }
    } else {
        pos.value = { x: window.innerWidth - 72, y: 120 }
    }
}

const handleResize = () => {
    pos.value = {
        x: clamp(pos.value.x, 0, window.innerWidth - 52),
        y: clamp(pos.value.y, 0, window.innerHeight - 52),
    }
}

onMounted(() => {
    positionAtSongViewer()
    syncCanvas()
    window.addEventListener('resize', handleResize)
    document.addEventListener('click', onClickOutside)
})

onUnmounted(() => {
    window.removeEventListener('resize', handleResize)
    document.removeEventListener('click', onClickOutside)
    clearTimeout(hoverTimer.value)
    clearTimeout(closeTimer.value)
})

defineExpose({
    getPosition: () => pos.value,
    resetPosition: positionAtSongViewer,
})
</script>

<style scoped>
.drawing-toolbar-trigger {
    width: 48px;
    height: 48px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: grab;
    touch-action: none;
    color: #e2e8f0;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.drawing-toolbar-trigger:active {
    cursor: grabbing;
}

.drawing-toolbar-trigger.is-active {
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.25), 0 8px 20px rgba(0, 0, 0, 0.25);
}

.drawing-toolbar-menu {
    position: absolute;
    left: 24px;
    top: 24px;
    width: 0;
    height: 0;
    z-index: 80;
}

.drawing-toolbar-item {
    position: absolute;
    left: -20px;
    top: -20px;
    width: 40px;
    height: 40px;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #cbd5e1;
    background: #334155;
    border: 1px solid #475569;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}

.drawing-toolbar-item:hover {
    background: #475569;
    color: #ffffff;
}

.drawing-toolbar-item.is-active {
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #ffffff;
    border-color: transparent;
}

.drawing-toolbar-linear {
    position: absolute;
    left: 56px;
    top: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border-radius: 12px;
    background: #1e293b;
    border: 1px solid #475569;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
    z-index: 80;
}

.drawing-toolbar-linear .drawing-toolbar-item {
    position: static;
}

.drawing-toolbar-slider-label {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #cbd5e1;
    padding: 0 4px;
}

.drawing-toolbar-slider {
    height: 4px;
    border-radius: 9999px;
    background: #475569;
    cursor: pointer;
    accent-color: #8b5cf6;
}

.drawing-toolbar-panel {
    position: absolute;
    left: 50%;
    top: 130px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 10px;
    background: #1e293b;
    border: 1px solid #475569;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
    z-index: 80;
}
</style>
